import { z } from "zod";
import type { IHubCatalog } from "./HubCatalog.types";
import type { ICatalogInstallConfig } from "./HubCatalogInstall.types";
import type { IHubCatalogType } from "./HubCatalogType.types";

/** Nested install config is keyed by catalog type name (e.g. `ipa`). */
export function resolveCatalogInstallTypeKey(
  catalog: IHubCatalog,
  catalogTypes: IHubCatalogType[] = [],
): string {
  const fromCatalog = (catalog.catalogTypeName ?? "").trim();
  if (fromCatalog) return fromCatalog;
  return (
    catalogTypes
      .find((type) => (type.typeId ?? 0) === (catalog.typeId ?? 0))
      ?.name?.trim() ?? ""
  );
}

const catalogFormFieldPayloadSchema = z.record(z.string(), z.unknown());

/** Zod shape aligned with {@link ICatalogInstallConfig}. */
export const catalogInstallConfigSchema: z.ZodType<ICatalogInstallConfig> =
  z.object({
    catalogId: z.number(),
    catalogVersion: z.number(),
    companyId: z.number(),
    catalogTypeId: z.number(),
    token: z.string(),
    name: z.string(),
    label: z.string(),
    description: z.string(),
    config: catalogFormFieldPayloadSchema.optional(),
  });

/** One array entry per catalog wizard step (solution, then each add-on). */
export function catalogInstallConfigArraySchemaForSteps(stepCount: number) {
  return z.array(catalogInstallConfigSchema).length(stepCount);
}

export function buildDefaultCatalogInstallConfigs(params: {
  solution: IHubCatalog | undefined;
  addons: IHubCatalog[];
  companyId: number;
  catalogTypes?: IHubCatalogType[];
}): ICatalogInstallConfig[] {
  const { solution, addons, companyId, catalogTypes = [] } = params;
  const toRow = (catalog: IHubCatalog): ICatalogInstallConfig => {
    const typeKey = resolveCatalogInstallTypeKey(catalog, catalogTypes);
    return {
      catalogId: catalog.catalogId ?? 0,
      catalogVersion: catalog.version ?? 0,
      companyId,
      catalogTypeId: catalog.typeId ?? 0,
      token: catalog.token ?? "",
      name: catalog.name ?? "",
      label: catalog.label ?? "",
      description: catalog.description ?? "",
      config: typeKey ? { [typeKey]: {} } : {},
    };
  };

  const rows: ICatalogInstallConfig[] = [];
  if (solution) rows.push(toRow(solution));
  for (const addon of addons) {
    rows.push(toRow(addon));
  }
  return rows;
}
